import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ChevronLeft, Users, Mail, UserPlus, Trash2, RefreshCw, ShieldAlert, Lock, Copy,
  Eye, EyeOff, Loader2, CheckCircle2, XCircle, AlertTriangle, Send, Crown, User,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const ROLES = [
  { value: "employee", label: "Employee", desc: "View shop info only." },
  { value: "manager",  label: "Manager",  desc: "Edit shop, hours, credentials — but no team or ownership control." },
  { value: "driver",   label: "Driver",   desc: "Sees only the deliveries you assign to them." },
];

function RolePill({ role }) {
  const cls = role === "owner"
    ? "bg-pink-500/20 border-pink-400/50 text-pink-100"
    : role === "manager"
      ? "bg-cyan-500/15 border-cyan-400/40 text-cyan-100"
      : "bg-white/5 border-white/10 text-zinc-200";
  const Icon = role === "owner" ? Crown : User;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${cls}`} data-testid={`team-role-${role}`}>
      <Icon className="w-3 h-3" /> {role}
    </span>
  );
}

export default function VendorTeam() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const businessId = useMemo(() => (user?.user_id ? `biz_${user.user_id}` : null), [user]);

  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "employee", public_display: false, public_title: "" });
  const [inviting, setInviting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // Ownership transfer dialog
  const [showTransfer, setShowTransfer] = useState(false);
  const [xf, setXf] = useState({ target_email: "", password: "", reason: "", confirmation_text: "", require_admin_approval: true });
  const [transferring, setTransferring] = useState(false);

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const [tRes, iRes, xRes] = await Promise.all([
        api.get(`/businesses/${businessId}/team/all`).catch((e) => e.response?.status === 403 ? { data: { members: [] } } : Promise.reject(e)),
        api.get(`/businesses/${businessId}/team/invitations`).catch((e) => e.response?.status === 403 ? { data: { items: [] } } : Promise.reject(e)),
        api.get(`/businesses/${businessId}/ownership/transfers`).catch(() => ({ data: { items: [] } })),
      ]);
      setMembers(tRes.data.members || []);
      setInvitations(iRes.data.items || []);
      setTransfers(xRes.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load team");
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  if (user && !user.is_vendor) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-3" data-testid="team-not-vendor">
        <Lock className="w-10 h-10 text-zinc-500 mx-auto" />
        <h1 className="text-3xl font-black tracking-tighter">Vendors only</h1>
        <p className="text-sm text-zinc-400">Open a shop first to build a team.</p>
        <Button onClick={() => navigate("/sell")} className="rounded-full bg-pink-500 hover:bg-pink-400 text-white h-11 px-5">Open Sell</Button>
      </div>
    );
  }

  const invite = async () => {
    if (!inviteForm.email.trim()) return toast.error("Enter the invitee's email address.");
    setInviting(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/team/invitations`, {
        email: inviteForm.email.trim(),
        role: inviteForm.role,
        public_display: inviteForm.public_display,
        public_title: inviteForm.public_title || null,
      });
      toast.success(data.reused ? "Existing invitation refreshed & resent." : "Invitation sent.");
      setInviteForm({ email: "", role: "employee", public_display: false, public_title: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send invitation");
    } finally {
      setInviting(false);
    }
  };

  const resend = async (inv) => {
    setBusyId(inv.invitation_id);
    try {
      await api.post(`/businesses/${businessId}/team/invitations/${inv.invitation_id}/resend`);
      toast.success("Invitation resent.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't resend");
    } finally { setBusyId(null); }
  };

  const cancelInv = async (inv) => {
    if (!window.confirm(`Cancel the invitation to ${inv.email}?`)) return;
    setBusyId(inv.invitation_id);
    try {
      await api.delete(`/businesses/${businessId}/team/invitations/${inv.invitation_id}`);
      toast.success("Invitation cancelled.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't cancel");
    } finally { setBusyId(null); }
  };

  const setMemberRole = async (m, role) => {
    if (m.role === "owner") return toast.error("Use the ownership transfer flow to move ownership.");
    setBusyId(m.team_member_id);
    try {
      await api.put(`/businesses/${businessId}/team/${m.team_member_id}`, { role });
      toast.success(`Role set to ${role}.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't change role");
    } finally { setBusyId(null); }
  };

  const togglePublicMember = async (m) => {
    setBusyId(m.team_member_id);
    try {
      await api.put(`/businesses/${businessId}/team/${m.team_member_id}`, { public_display: !m.public_display });
      toast.success(!m.public_display ? "Now shown on public storefront." : "Hidden from public storefront.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update visibility");
    } finally { setBusyId(null); }
  };

  const removeMember = async (m) => {
    if (m.role === "owner") return toast.error("Owner cannot be removed.");
    if (!window.confirm(`Remove ${m.user?.name || m.user_id} from your team? Their history stays in the audit log.`)) return;
    setBusyId(m.team_member_id);
    try {
      await api.delete(`/businesses/${businessId}/team/${m.team_member_id}`);
      toast.success("Team member removed.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove member");
    } finally { setBusyId(null); }
  };

  const submitTransfer = async () => {
    if (!xf.target_email.trim()) return toast.error("Enter the target owner's email.");
    if (!xf.password) return toast.error("Enter your current password to authorize.");
    if (xf.confirmation_text !== "TRANSFER") return toast.error('Type "TRANSFER" exactly to confirm.');
    setTransferring(true);
    try {
      await api.post(`/businesses/${businessId}/ownership/transfer`, {
        target_email: xf.target_email.trim(),
        password: xf.password,
        reason: xf.reason || null,
        confirmation_text: "TRANSFER",
        require_admin_approval: xf.require_admin_approval,
      });
      toast.success("Ownership transfer initiated. Awaiting recipient acceptance.");
      setShowTransfer(false);
      setXf({ target_email: "", password: "", reason: "", confirmation_text: "", require_admin_approval: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't initiate transfer");
    } finally { setTransferring(false); }
  };

  const cancelTransfer = async (t) => {
    if (!window.confirm("Cancel this pending ownership transfer?")) return;
    try {
      await api.post(`/ownership-transfers/${t.transfer_id}/cancel`);
      toast.success("Transfer cancelled.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't cancel");
    }
  };

  // Determine if current user is an owner
  const myTm = members.find((m) => m.user_id === user?.user_id);
  const iAmOwner = myTm?.role === "owner";
  const activeMembers = members.filter((m) => m.status !== "removed");
  const pendingInvitations = invitations.filter((i) => i.status === "pending");
  const pastInvitations = invitations.filter((i) => i.status !== "pending");
  const openTransfers = transfers.filter((t) => ["pending_target", "pending_admin"].includes(t.status));

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-24 space-y-4" data-testid="vendor-team-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="team-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <header className="rounded-3xl border border-cyan-400/30 bg-gradient-to-br from-cyan-500/10 via-zinc-950 to-pink-500/10 p-5">
        <p className="inline-flex items-center gap-2 text-cyan-300 text-[10px] uppercase tracking-[0.3em] font-black">
          <Users className="w-3 h-3" /> Shop Team
        </p>
        <h1 className="text-3xl font-black tracking-tighter mt-1">Manage your team</h1>
        <p className="text-sm text-zinc-400 mt-1">Invite managers and employees. Every role change and removal is audited on the business record.</p>
      </header>

      {loading ? (
        <div className="py-6 text-center text-zinc-500 inline-flex items-center gap-2 justify-center w-full text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading team…</div>
      ) : !iAmOwner ? (
        <div className="rounded-3xl bg-amber-500/10 border border-amber-400/30 p-5 text-amber-100" data-testid="team-not-owner">
          <p className="font-black tracking-tight uppercase text-[10px]">Owner-only</p>
          <p className="text-sm mt-1">Only the shop owner can manage the team. Your current role is <b>{myTm?.role || "not a team member"}</b>.</p>
        </div>
      ) : (
        <>
          {/* Invite */}
          <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5 space-y-3" data-testid="team-invite-card">
            <h2 className="text-lg font-black tracking-tight inline-flex items-center gap-2"><UserPlus className="w-4 h-4" /> Invite someone</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Email</Label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="teammate@email.com" className="pl-9 h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="team-invite-email" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Role</Label>
                <div className="flex flex-col gap-1.5">
                  {ROLES.map((r) => (
                    <label key={r.value} className={`rounded-2xl border p-3 cursor-pointer text-xs ${inviteForm.role === r.value ? "border-cyan-400/50 bg-cyan-500/10" : "border-white/10 bg-white/[0.02]"}`} data-testid={`team-invite-role-${r.value}`}>
                      <input type="radio" name="invite_role" checked={inviteForm.role === r.value} onChange={() => setInviteForm({ ...inviteForm, role: r.value })} className="mr-2 accent-cyan-500" />
                      <span className="font-black text-sm capitalize">{r.label}</span>
                      <p className="text-zinc-400 mt-0.5">{r.desc}</p>
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Public title (optional)</Label>
                <Input value={inviteForm.public_title} onChange={(e) => setInviteForm({ ...inviteForm, public_title: e.target.value })} placeholder="Head chef, Delivery lead…" maxLength={60} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="team-invite-public-title" />
                <label className="flex items-center gap-2 text-xs text-zinc-300 mt-2" data-testid="team-invite-public-toggle-label">
                  <input type="checkbox" checked={inviteForm.public_display} onChange={(e) => setInviteForm({ ...inviteForm, public_display: e.target.checked })} className="accent-cyan-500" data-testid="team-invite-public-toggle" />
                  Show on public storefront when they join
                </label>
              </div>
            </div>
            <Button onClick={invite} disabled={inviting} className="w-full h-12 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white font-bold" data-testid="team-invite-submit">
              {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send invitation
            </Button>
            <p className="text-[10px] text-zinc-500">The invitee gets a Hidden Gem notification. They must sign in with this email and accept before they gain any permissions.</p>
          </section>

          {/* Pending invitations */}
          <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="team-invitations-card">
            <h2 className="text-lg font-black tracking-tight">Pending invitations ({pendingInvitations.length})</h2>
            {pendingInvitations.length === 0 ? (
              <p className="text-sm text-zinc-500 mt-2">Nothing pending.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {pendingInvitations.map((inv) => (
                  <div key={inv.invitation_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 flex items-center gap-3 flex-wrap" data-testid={`team-invitation-${inv.invitation_id}`}>
                    <Mail className="w-4 h-4 text-cyan-300 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate">{inv.email}</p>
                      <p className="text-[11px] text-zinc-400 capitalize">{inv.role} · sent {new Date(inv.created_at).toLocaleDateString()} · expires {new Date(inv.expires_at).toLocaleDateString()}</p>
                    </div>
                    <button
                      onClick={() => resend(inv)}
                      disabled={busyId === inv.invitation_id}
                      className="h-8 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-60"
                      data-testid={`team-invitation-resend-${inv.invitation_id}`}
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Resend
                    </button>
                    <button
                      onClick={() => cancelInv(inv)}
                      disabled={busyId === inv.invitation_id}
                      className="h-8 px-3 rounded-full bg-rose-500/15 hover:bg-rose-500/25 border border-rose-400/40 text-rose-100 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-60"
                      data-testid={`team-invitation-cancel-${inv.invitation_id}`}
                    >
                      <XCircle className="w-3.5 h-3.5" /> Cancel
                    </button>
                  </div>
                ))}
              </div>
            )}
            {pastInvitations.length > 0 && (
              <details className="mt-4">
                <summary className="text-xs text-zinc-400 cursor-pointer" data-testid="team-past-invitations-toggle">Past invitations ({pastInvitations.length})</summary>
                <div className="mt-2 space-y-1">
                  {pastInvitations.slice(0, 20).map((inv) => (
                    <div key={inv.invitation_id} className="text-[11px] text-zinc-400 flex items-center gap-2" data-testid={`team-past-invitation-${inv.invitation_id}`}>
                      <span className="w-2 h-2 rounded-full bg-zinc-600" />
                      <span className="capitalize">{inv.status}</span>
                      <span className="truncate">{inv.email}</span>
                      <span className="ml-auto">{new Date(inv.updated_at || inv.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </section>

          {/* Active team */}
          <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="team-active-card">
            <h2 className="text-lg font-black tracking-tight">Active team ({activeMembers.length})</h2>
            <div className="mt-3 space-y-2">
              {activeMembers.map((m) => (
                <div key={m.team_member_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-3" data-testid={`team-member-${m.team_member_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 border border-white/10 shrink-0">
                      {m.user?.avatar_url ? (
                        <img src={fileUrl(m.user.avatar_url)} alt={m.user?.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-zinc-400 text-xs font-bold">{(m.user?.name || m.user?.email || "?").slice(0,1).toUpperCase()}</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold truncate">{m.user?.name || m.user?.email || m.user_id}</p>
                        <RolePill role={m.role} />
                        {m.public_display && <span className="text-[10px] text-emerald-300 font-black uppercase tracking-widest">Public</span>}
                      </div>
                      <p className="text-[11px] text-zinc-500 truncate">{m.user?.email}</p>
                    </div>
                  </div>
                  {m.role !== "owner" && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 pl-13">
                      <select
                        value={m.role}
                        onChange={(e) => setMemberRole(m, e.target.value)}
                        disabled={busyId === m.team_member_id}
                        className="h-8 rounded-full bg-white/5 border border-white/10 text-xs px-3 text-white disabled:opacity-60"
                        data-testid={`team-member-role-${m.team_member_id}`}
                      >
                        {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                      <button
                        onClick={() => togglePublicMember(m)}
                        disabled={busyId === m.team_member_id}
                        className={`h-8 px-3 rounded-full border text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60 ${
                          m.public_display ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-100" : "bg-white/5 border-white/10 text-zinc-300"
                        }`}
                        data-testid={`team-member-toggle-public-${m.team_member_id}`}
                      >
                        {m.public_display ? <><Eye className="w-3.5 h-3.5" /> On storefront</> : <><EyeOff className="w-3.5 h-3.5" /> Private</>}
                      </button>
                      <button
                        onClick={() => removeMember(m)}
                        disabled={busyId === m.team_member_id}
                        className="h-8 px-3 rounded-full bg-rose-500/15 hover:bg-rose-500/25 border border-rose-400/40 text-rose-100 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60 ml-auto"
                        data-testid={`team-member-remove-${m.team_member_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Ownership Transfer — Danger Zone */}
          <section className="rounded-3xl bg-gradient-to-br from-rose-500/10 to-transparent border border-rose-400/40 p-5 space-y-3" data-testid="team-ownership-card">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-rose-300" />
              <h2 className="text-lg font-black tracking-tight text-rose-100">Danger zone — Ownership</h2>
            </div>
            <p className="text-sm text-zinc-300">
              Transferring ownership moves control of the entire business — products, orders, reviews, credentials, team, and wallet payouts — to another person. Your account keeps its history and is demoted to Manager.
            </p>

            {openTransfers.length > 0 && (
              <div className="rounded-2xl bg-amber-500/10 border border-amber-400/40 p-4" data-testid="team-open-transfer">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-300" />
                  <p className="text-sm font-black text-amber-100">Ownership transfer in progress</p>
                </div>
                {openTransfers.map((t) => (
                  <div key={t.transfer_id} className="mt-2 text-xs text-amber-100" data-testid={`team-transfer-${t.transfer_id}`}>
                    <p>To: <b>{t.target_email}</b></p>
                    <p>Status: <span className="capitalize">{t.status.replace("_", " ")}</span> · started {new Date(t.created_at).toLocaleDateString()}</p>
                    <button
                      onClick={() => cancelTransfer(t)}
                      className="mt-2 h-8 px-3 rounded-full bg-rose-500 hover:bg-rose-400 text-white text-xs font-bold inline-flex items-center gap-1"
                      data-testid={`team-cancel-transfer-${t.transfer_id}`}
                    >
                      <XCircle className="w-3.5 h-3.5" /> Cancel transfer
                    </button>
                  </div>
                ))}
              </div>
            )}

            <Button
              onClick={() => setShowTransfer(true)}
              disabled={openTransfers.length > 0}
              className="w-full h-12 rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold disabled:opacity-50"
              data-testid="team-transfer-open"
            >
              <Crown className="w-4 h-4 mr-2" /> Transfer ownership
            </Button>

            {transfers.length > 0 && (
              <details className="mt-2">
                <summary className="text-xs text-zinc-400 cursor-pointer" data-testid="team-transfer-history-toggle">Transfer history ({transfers.length})</summary>
                <div className="mt-2 space-y-1">
                  {transfers.slice(0, 10).map((t) => (
                    <div key={t.transfer_id} className="text-[11px] text-zinc-400 flex items-center gap-2" data-testid={`team-transfer-history-${t.transfer_id}`}>
                      <span className="capitalize">{t.status.replace("_", " ")}</span>
                      <span>→ {t.target_email}</span>
                      <span className="ml-auto">{new Date(t.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </section>
        </>
      )}

      {/* Transfer dialog */}
      {showTransfer && (
        <div className="fixed inset-0 z-[95] grid place-items-center bg-black/85 backdrop-blur-sm p-4" onClick={() => setShowTransfer(false)} data-testid="team-transfer-modal">
          <div className="w-full max-w-lg bg-zinc-950 rounded-3xl border border-rose-400/60 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-rose-300" />
                <h3 className="text-lg font-black tracking-tight">Transfer ownership</h3>
              </div>
              <p className="text-xs text-zinc-400 mt-1.5">This is a high-risk action. You'll stay on as a Manager. All shop data, orders, reviews, credentials, and wallet balances are preserved.</p>
            </div>
            <div className="p-5 space-y-3">
              <div className="rounded-2xl bg-amber-500/10 border border-amber-400/40 p-3 text-xs text-amber-100">
                <p><b>What stays intact:</b> products, orders, reviews, reputation, followers, credentials, team roster history, wallet & payout records, shop URL, and QR code.</p>
                <p className="mt-1"><b>What changes:</b> Only the human who signs off on payouts and controls team management. You keep every historical action tied to your account.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Target owner email</Label>
                <Input type="email" value={xf.target_email} onChange={(e) => setXf({ ...xf, target_email: e.target.value })} placeholder="new-owner@email.com" className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="team-transfer-email" />
                <p className="text-[10px] text-zinc-500">Must already have a Hidden Gem account.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Reason (optional)</Label>
                <Input value={xf.reason} onChange={(e) => setXf({ ...xf, reason: e.target.value })} placeholder="Selling the business, retiring…" maxLength={400} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="team-transfer-reason" />
              </div>
              <label className="flex items-start gap-2 text-xs text-zinc-300" data-testid="team-transfer-admin-label">
                <input type="checkbox" checked={xf.require_admin_approval} onChange={(e) => setXf({ ...xf, require_admin_approval: e.target.checked })} className="mt-0.5 accent-rose-500" data-testid="team-transfer-admin-toggle" />
                <span>
                  <b>Require admin approval</b> before ownership actually changes. Recommended — a Hidden Gem admin will review and finalize after the recipient accepts.
                </span>
              </label>
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Your current password</Label>
                <Input type="password" value={xf.password} onChange={(e) => setXf({ ...xf, password: e.target.value })} placeholder="Re-enter your Hidden Gem password" autoComplete="current-password" className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="team-transfer-password" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Type &quot;TRANSFER&quot; to confirm</Label>
                <Input value={xf.confirmation_text} onChange={(e) => setXf({ ...xf, confirmation_text: e.target.value })} placeholder="TRANSFER" className="h-11 bg-white/5 border-white/10 rounded-2xl text-white uppercase tracking-widest" data-testid="team-transfer-confirmation" />
              </div>
            </div>
            <div className="p-4 border-t border-white/10 flex items-center gap-2 justify-end">
              <button onClick={() => setShowTransfer(false)} className="h-10 px-4 rounded-full bg-white/5 hover:bg-white/10 text-sm font-bold" data-testid="team-transfer-cancel">Cancel</button>
              <Button
                onClick={submitTransfer}
                disabled={transferring || xf.confirmation_text !== "TRANSFER" || !xf.password || !xf.target_email}
                className="h-10 px-5 rounded-full bg-rose-500 hover:bg-rose-400 text-white text-sm font-bold disabled:opacity-50"
                data-testid="team-transfer-submit"
              >
                {transferring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Crown className="w-4 h-4 mr-2" />}
                Initiate transfer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
